/**
 * Expanded Skill Database for All Class Categories
 *
 * Provides skill suggestions organized by class category and level.
 * Used by the SkillSelector component to show relevant skill suggestions
 * when a gym owner creates or edits any class type.
 *
 * Created: 2026-02-20
 * Updated: 2026-02-23 — Full industry audit:
 *   - Fixed difficulty ratings to match real-world USASF progression
 *   - Added explicit difficulty for ALL skills (removed L5 fallback issues)
 *   - Fixed spelling ("Aeriel" → "Aerial")
 *   - Removed ambiguous "Back Handspring" (use Standing BHS or Round-off BHS)
 *   - Corrected level suggestions to match real gym class expectations
 *   - Added clarifications for obscure skill names (Randy, Miller, etc.)
 */

import type { ClassCategory, ClassLevel } from './classTypes';

export interface SkillOption {
  name: string;
  category: string;
  difficulty: number;
  isCustom?: boolean;
}

export interface LevelSkills {
  required_suggestions: readonly string[];
  mastery_suggestions: readonly string[];
  optional: readonly string[];
}

export interface CategorySkillData {
  levels: Record<string, LevelSkills>;
  categories: Record<string, readonly string[]>;
  difficulty: Record<string, number>;
}

// ---------------------------------------------------------------------------
// TUMBLING
// Progression: Rolls → Cartwheels → Walkovers → Handsprings → Tucks → Layouts → Twists
// ---------------------------------------------------------------------------

const TUMBLING_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Forward Roll', 'Backward Roll', 'Cartwheel', 'Handstand (5 seconds)'],
      mastery_suggestions: ['Handstand (30 seconds)', 'Bridge Kickover', 'Round-off', 'Back Walkover (with assistance)'],
      optional: ['Log Roll', 'Shoulder Roll', 'Straddle Roll', 'Straight Arm Bridge', 'Wall Handstand'],
    },
    Intermediate: {
      required_suggestions: ['Round-off', 'Bridge Kickover', 'Back Walkover (unassisted)', 'Front Walkover'],
      mastery_suggestions: ['Front Handspring', 'Round-off Back Handspring', 'Standing Back Handspring', 'Back Handspring Step-out'],
      optional: ['One-handed Cartwheel', 'Aerial Cartwheel', 'Back Limber', 'Front Limber', 'Valdez'],
    },
    Advanced: {
      required_suggestions: ['Standing Back Handspring', 'Front Handspring', 'Standing Back Handspring Series', 'Round-off Back Handspring Back Handspring'],
      mastery_suggestions: ['Back Tuck', 'Front Tuck', 'Round-off Back Handspring Back Tuck', 'Standing Back Tuck'],
      optional: ['Back Pike', 'Front Pike', 'Side Somi', 'Aerial Walkover'],
    },
    Elite: {
      required_suggestions: ['Back Tuck', 'Front Tuck', 'Round-off Back Handspring Back Tuck', 'Layout'],
      mastery_suggestions: ['Full Twist', 'Round-off Back Handspring Layout', 'Front Layout', 'Double Full'],
      optional: ['Half-in Half-out', 'Arabian Front', 'Randy (Layout 1.5 Twist)', 'Miller (Double Back with Twist)', 'Whip Double'],
    },
  },
  categories: {
    'Basic Rolls & Positions': ['Forward Roll', 'Backward Roll', 'Log Roll', 'Shoulder Roll', 'Straddle Roll', 'Pike Roll'],
    'Handstands & Static Skills': ['Handstand (5 seconds)', 'Handstand (30 seconds)', 'Handstand (1 minute)', 'Wall Handstand', 'Handstand Pushup'],
    'Cartwheels & Variations': ['Cartwheel', 'One-handed Cartwheel', 'Aerial Cartwheel', 'Side Handstand', 'Round-off'],
    'Bridges & Flexibility': ['Straight Arm Bridge', 'Bridge Kickover', 'Back Walkover (with assistance)', 'Back Walkover (unassisted)', 'Back Walkover Step-out', 'Back Limber', 'Front Limber', 'Valdez'],
    'Front Skills': ['Front Walkover', 'Front Handspring', 'Front Tuck', 'Front Layout', 'Front Pike'],
    'Back Handsprings': ['Standing Back Handspring', 'Round-off Back Handspring', 'Back Handspring Step-out', 'Standing Back Handspring Series', 'Round-off Back Handspring Back Handspring'],
    'Flipping Skills': ['Back Tuck', 'Standing Back Tuck', 'Back Pike', 'Layout', 'Whip Back', 'Side Somi'],
    'Twisting Skills': ['Full Twist', 'Double Full', 'Triple Full'],
    'Advanced Skills': ['Double Back Tuck', 'Double Layout', 'Arabian Front', 'Arabian Double Front', 'Half-in Half-out', 'Randy (Layout 1.5 Twist)', 'Miller (Double Back with Twist)'],
    'Combination Passes': ['Round-off Back Handspring Back Tuck', 'Round-off Back Handspring Layout', 'Round-off Back Handspring Full Twist', 'Whip Double', 'Back Handspring Back Tuck'],
  },
  difficulty: {
    // L1 — Basic rolls
    'Forward Roll': 1, 'Backward Roll': 1, 'Log Roll': 1, 'Shoulder Roll': 1, 'Straddle Roll': 1, 'Pike Roll': 1,
    // L2 — Cartwheel family and basics
    'Cartwheel': 2, 'Handstand (5 seconds)': 2, 'Straight Arm Bridge': 2, 'Wall Handstand': 2,
    // L3 — Round-off and assisted walkovers
    'Round-off': 3, 'Handstand (30 seconds)': 3, 'Back Walkover (with assistance)': 3, 'Side Handstand': 3,
    // L4 — Bridge kickover, limbers, one-handed skills
    'Bridge Kickover': 4, 'One-handed Cartwheel': 4, 'Back Limber': 4, 'Front Limber': 4, 'Handstand (1 minute)': 4,
    // L5 — Unassisted walkovers
    'Back Walkover (unassisted)': 5, 'Front Walkover': 5, 'Back Walkover Step-out': 5, 'Handstand Pushup': 5,
    // L6 — Handsprings (with momentum) and aerial variations
    'Front Handspring': 6, 'Round-off Back Handspring': 6, 'Aerial Cartwheel': 6, 'Valdez': 6, 'Back Handspring Step-out': 6,
    // L7 — Standing back handspring (harder, no momentum), series
    'Standing Back Handspring': 7, 'Standing Back Handspring Series': 7, 'Round-off Back Handspring Back Handspring': 7, 'Aerial Walkover': 7,
    // L8 — Tucks and whips
    'Back Tuck': 8, 'Front Tuck': 8, 'Standing Back Tuck': 8, 'Whip Back': 8, 'Back Pike': 8, 'Front Pike': 8, 'Side Somi': 8,
    // L9 — Layouts, fulls, combination passes
    'Layout': 9, 'Full Twist': 9, 'Front Layout': 9,
    'Round-off Back Handspring Back Tuck': 9, 'Round-off Back Handspring Layout': 9, 'Back Handspring Back Tuck': 9,
    // L10 — Doubles and elite twisting
    'Double Back Tuck': 10, 'Double Layout': 10, 'Double Full': 10, 'Triple Full': 10,
    'Round-off Back Handspring Full Twist': 10, 'Whip Double': 10,
    'Half-in Half-out': 10, 'Arabian Front': 10, 'Arabian Double Front': 10,
    'Randy (Layout 1.5 Twist)': 10, 'Miller (Double Back with Twist)': 10,
  },
};

// ---------------------------------------------------------------------------
// STUNTING (general stunting skills for bases, backs, and flyers together)
// Progression: Thigh Stand → Prep → Extension → Body Positions → Advanced Entries
// ---------------------------------------------------------------------------

const STUNTING_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Thigh Stand', 'Shoulder Sit', 'Prep-Level Stunt (two-leg)', 'Ground-Up Prep'],
      mastery_suggestions: ['Prep-Level Liberty', 'Prep-Level Cradle', 'Sponge Toss', 'Toss Hands'],
      optional: ['Reload Prep', 'Walk-in Prep', 'Pop Cradle'],
    },
    Intermediate: {
      required_suggestions: ['Extension (two-leg)', 'Prep-Level Liberty', 'Cradle from Extension', 'Reload Extension'],
      mastery_suggestions: ['Extension Liberty', 'Extension Heel Stretch', 'Twist Cradle', 'Full-Around Liberty'],
      optional: ['Walk-in Extension', 'Basket Toss', 'Pop Cradle'],
    },
    Advanced: {
      required_suggestions: ['Extension Liberty', 'Extension Heel Stretch', 'Full-Up to Extension', 'Twist Cradle'],
      mastery_suggestions: ['Scorpion', 'Bow and Arrow', 'Switch-Up Liberty', 'Full-Down Cradle'],
      optional: ['Arabesque', 'Scale', 'Tick-Tock Heel Stretch to Lib'],
    },
    Elite: {
      required_suggestions: ['Full-Up to Heel Stretch', 'Double-Down Cradle', 'Tick-Tock Heel Stretch to Lib', 'Rewind to Extension'],
      mastery_suggestions: ['Double-Up', 'Kick Full Basket', 'Rewind to Liberty', '2-2-1 Pyramid Transition'],
      optional: ['Triple-Down', 'Switch-Up Heel Stretch', 'Standing Full-Up'],
    },
  },
  categories: {
    'Prep-Level Stunts': ['Thigh Stand', 'Shoulder Sit', 'Prep-Level Stunt (two-leg)', 'Prep-Level Liberty', 'Prep-Level Cradle', 'Walk-in Prep', 'Ground-Up Prep'],
    'Extension Stunts': ['Extension (two-leg)', 'Extension Liberty', 'Extension Heel Stretch', 'Reload Extension', 'Walk-in Extension'],
    'Body Positions': ['Liberty', 'Heel Stretch', 'Scorpion', 'Bow and Arrow', 'Arabesque', 'Scale', 'Needle'],
    'Transitions': ['Tick-Tock Heel Stretch to Lib', 'Switch-Up Liberty', 'Full-Around Liberty', 'Switch-Up Heel Stretch', 'Reload Prep'],
    'Dismounts': ['Cradle from Extension', 'Twist Cradle', 'Full-Down Cradle', 'Double-Down Cradle', 'Triple-Down', 'Pop Cradle'],
    'Advanced Entries': ['Full-Up to Extension', 'Full-Up to Heel Stretch', 'Rewind to Extension', 'Double-Up', 'Standing Full-Up', 'Rewind to Liberty'],
    'Tosses': ['Sponge Toss', 'Toss Hands', 'Basket Toss', 'Kick Full Basket', 'Kick Double Basket'],
    'Pyramids': ['2-2-1 Pyramid Transition', 'Braced Flip', 'Pyramid Release Move'],
  },
  difficulty: {
    // L1 — Entry stunts
    'Thigh Stand': 1, 'Shoulder Sit': 1, 'Sponge Toss': 1,
    // L2 — Prep basics
    'Prep-Level Stunt (two-leg)': 2, 'Toss Hands': 2, 'Walk-in Prep': 2,
    // L3 — Prep body positions and cradles
    'Prep-Level Liberty': 3, 'Prep-Level Cradle': 3, 'Ground-Up Prep': 3, 'Pop Cradle': 3, 'Reload Prep': 3,
    // L4 — Extensions
    'Extension (two-leg)': 4, 'Reload Extension': 4, 'Liberty': 4,
    // L5 — Extension dismounts and basket toss
    'Cradle from Extension': 5, 'Walk-in Extension': 5, 'Basket Toss': 5,
    // L6 — Extension body positions and twist dismounts
    'Extension Liberty': 6, 'Extension Heel Stretch': 6, 'Twist Cradle': 6,
    'Full-Around Liberty': 6, 'Heel Stretch': 6, 'Arabesque': 6, 'Scale': 6,
    // L7 — Advanced positions and entries
    'Scorpion': 7, 'Bow and Arrow': 7, 'Full-Up to Extension': 7, 'Switch-Up Liberty': 7, 'Full-Down Cradle': 7,
    'Full-Up to Heel Stretch': 7, 'Tick-Tock Heel Stretch to Lib': 7,
    // L8 — Elite dismounts and body positions
    'Needle': 8, 'Double-Down Cradle': 8, 'Kick Full Basket': 8, 'Switch-Up Heel Stretch': 8,
    // L9 — Elite entries and pyramids
    'Rewind to Extension': 9, 'Double-Up': 9, 'Rewind to Liberty': 9,
    '2-2-1 Pyramid Transition': 9, 'Pyramid Release Move': 9,
    // L10 — Extreme elite
    'Triple-Down': 10, 'Standing Full-Up': 10, 'Kick Double Basket': 10, 'Braced Flip': 10,
  },
};

// ---------------------------------------------------------------------------
// FLYER (position-specific skills for flyers)
// Progression: Body Position → Lock-Outs → Flexibility → Pull Technique → Transitions
// ---------------------------------------------------------------------------

const FLYER_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Tight Body Position', 'Prep-Level Lock-Out', 'Cradle Catch Position', 'Basic Liberty Hold (3 sec)'],
      mastery_suggestions: ['Prep-Level Liberty (5 sec)', 'Active Leg Flexibility Drill', 'Controlled Cradle', 'Ground-Level Heel Stretch'],
      optional: ['Core Hollow Hold', 'Ankle Flexibility Drill', 'Shoulder Flexibility'],
    },
    Intermediate: {
      required_suggestions: ['Extension Lock-Out', 'Extension Liberty (5 sec)', 'Extension Heel Stretch', 'Controlled Twist Cradle'],
      mastery_suggestions: ['Scorpion (held 3 sec)', 'Arabesque (held 3 sec)', 'Bow and Arrow', 'Full-Around Pull'],
      optional: ['Scale', 'Needle Prep', 'Standing Arabesque'],
    },
    Advanced: {
      required_suggestions: ['Scorpion (held 5 sec)', 'Needle', 'Full-Up Pull', 'Tick-Tock Heel Stretch to Lib'],
      mastery_suggestions: ['Switch-Up Pull', 'Bow and Arrow to Scorpion', 'Basket Toss Straight Ride', 'Full-Down Body Position'],
      optional: ['Over-Split', 'Standing Needle', 'Chin Stand'],
    },
    Elite: {
      required_suggestions: ['Rewind Pull', 'Double-Down Ride', 'Kick Full Basket Body Position', 'Tick-Tock Scorpion to Heel Stretch'],
      mastery_suggestions: ['Standing Needle (held 5 sec)', 'Kick Double Basket Position', 'Switch-Up to Scorpion', 'Multi-Transition Combo'],
      optional: ['Over-Split (past 180°)', 'Triple Full Basket Position'],
    },
  },
  categories: {
    'Body Positions': ['Tight Body Position', 'Liberty Hold', 'Heel Stretch', 'Scorpion', 'Bow and Arrow', 'Arabesque', 'Scale', 'Needle', 'Extension Heel Stretch'],
    'Lock-Outs & Technique': ['Prep-Level Lock-Out', 'Extension Lock-Out', 'Cradle Catch Position', 'Controlled Cradle', 'Controlled Twist Cradle'],
    'Flexibility': ['Ground-Level Heel Stretch', 'Active Leg Flexibility Drill', 'Shoulder Flexibility', 'Over-Split', 'Over-Split (past 180°)', 'Standing Needle', 'Chin Stand', 'Standing Arabesque', 'Needle Prep'],
    'Pull Technique': ['Full-Up Pull', 'Switch-Up Pull', 'Rewind Pull', 'Full-Around Pull'],
    'Transitions': ['Tick-Tock Heel Stretch to Lib', 'Tick-Tock Scorpion to Heel Stretch', 'Bow and Arrow to Scorpion', 'Switch-Up to Scorpion', 'Multi-Transition Combo'],
    'Toss Positions': ['Basket Toss Straight Ride', 'Kick Full Basket Body Position', 'Kick Double Basket Position', 'Full-Down Body Position', 'Double-Down Ride', 'Triple Full Basket Position'],
  },
  difficulty: {
    // L1 — Fundamental body awareness
    'Tight Body Position': 1, 'Core Hollow Hold': 1, 'Ankle Flexibility Drill': 1, 'Shoulder Flexibility': 1,
    // L2 — Prep-level technique
    'Prep-Level Lock-Out': 2, 'Cradle Catch Position': 2, 'Controlled Cradle': 2,
    'Ground-Level Heel Stretch': 2, 'Active Leg Flexibility Drill': 2,
    // L3 — Basic holds
    'Basic Liberty Hold (3 sec)': 3, 'Prep-Level Liberty (5 sec)': 3, 'Liberty Hold': 3,
    // L4 — Extension technique
    'Extension Lock-Out': 4, 'Needle Prep': 4, 'Standing Arabesque': 4,
    // L5 — Extension body positions
    'Extension Liberty (5 sec)': 5, 'Heel Stretch': 5, 'Controlled Twist Cradle': 5,
    'Arabesque (held 3 sec)': 5, 'Basket Toss Straight Ride': 5,
    // L6 — Advanced positions
    'Extension Heel Stretch': 6, 'Arabesque': 6, 'Scale': 6, 'Full-Around Pull': 6, 'Full-Down Body Position': 6,
    // L7 — Elite positions
    'Scorpion (held 3 sec)': 7, 'Scorpion': 7, 'Full-Up Pull': 7,
    'Bow and Arrow': 7, 'Tick-Tock Heel Stretch to Lib': 7, 'Over-Split': 7, 'Chin Stand': 7,
    // L8 — Elite technique
    'Needle': 8, 'Switch-Up Pull': 8, 'Scorpion (held 5 sec)': 8,
    'Double-Down Ride': 8, 'Standing Needle': 8, 'Bow and Arrow to Scorpion': 8,
    'Kick Full Basket Body Position': 8, 'Over-Split (past 180°)': 8,
    // L9 — Championship level
    'Rewind Pull': 9, 'Standing Needle (held 5 sec)': 9,
    'Switch-Up to Scorpion': 9, 'Multi-Transition Combo': 9, 'Tick-Tock Scorpion to Heel Stretch': 9,
    // L10 — Worlds level
    'Kick Double Basket Position': 10, 'Triple Full Basket Position': 10,
  },
};

// ---------------------------------------------------------------------------
// JUMPS & FLEXIBILITY
// Progression: Basic Jumps → Competition Jumps → Combo Jumps → Jump-to-Tumbling
// ---------------------------------------------------------------------------

const JUMPS_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Straight Jump', 'Tuck Jump', 'Spread Eagle', 'Herkie'],
      mastery_suggestions: ['Toe Touch (T-level)', 'Pike Jump', 'Right Hurdler', 'Left Hurdler'],
      optional: ['Pencil Jump', 'Stag Jump', 'Front Splits (work toward)'],
    },
    Intermediate: {
      required_suggestions: ['Toe Touch (competition standard)', 'Pike Jump', 'Right Hurdler', 'Left Hurdler'],
      mastery_suggestions: ['Toe Touch (hyperextended)', 'Pike (past 90°)', 'Switch Leap', 'Consecutive Hurdlers'],
      optional: ['C-Jump', 'Right Split (flat)', 'Left Split (flat)', 'Middle Split (work toward)'],
    },
    Advanced: {
      required_suggestions: ['Toe Touch (hyperextended)', 'Pike (past 90°)', 'Switch Leap', 'Consecutive Hurdlers'],
      mastery_suggestions: ['Toe Touch to Toe Touch', 'Pike to Toe Touch Series', 'Switch to Toe Touch', 'All 3 Splits (flat)'],
      optional: ['Over-Split', 'Triple Jump Combo', 'Needle Kick'],
    },
    Elite: {
      required_suggestions: ['Toe Touch to Toe Touch', 'Triple Jump Combo', 'All 3 Splits (flat)', 'Needle Kick'],
      mastery_suggestions: ['Jump to Tumbling Pass', 'Five-count Jump Series', 'All Splits Past Flat'],
      optional: ['Standing Back Tuck from Toe Touch', 'Jump Section Choreography'],
    },
  },
  categories: {
    'Basic Jumps': ['Straight Jump', 'Tuck Jump', 'Spread Eagle', 'Herkie', 'Pencil Jump', 'Stag Jump'],
    'Competition Jumps': ['Toe Touch (T-level)', 'Toe Touch (competition standard)', 'Toe Touch (hyperextended)', 'Pike Jump', 'Pike (past 90°)', 'Right Hurdler', 'Left Hurdler'],
    'Advanced Jumps': ['Switch Leap', 'Consecutive Hurdlers', 'C-Jump', 'Needle Kick'],
    'Jump Combinations': ['Toe Touch to Toe Touch', 'Pike to Toe Touch Series', 'Switch to Toe Touch', 'Triple Jump Combo', 'Five-count Jump Series'],
    'Flexibility': ['Front Splits (work toward)', 'Right Split (flat)', 'Left Split (flat)', 'Middle Split (work toward)', 'All 3 Splits (flat)', 'All Splits Past Flat', 'Over-Split', 'Standing Needle'],
  },
  difficulty: {
    // L1 — Entry jumps
    'Straight Jump': 1, 'Tuck Jump': 1, 'Spread Eagle': 1, 'Herkie': 1, 'Pencil Jump': 1,
    // L2 — Flexibility basics
    'Front Splits (work toward)': 2,
    // L3 — First competition jumps
    'Toe Touch (T-level)': 3, 'Pike Jump': 3, 'Right Hurdler': 3, 'Left Hurdler': 3, 'Stag Jump': 3,
    'Middle Split (work toward)': 3,
    // L4 — Competition standard
    'Toe Touch (competition standard)': 4, 'Right Split (flat)': 4, 'Left Split (flat)': 4,
    // L5 — Advanced singles
    'C-Jump': 5, 'Over-Split': 5,
    // L6 — High-level singles
    'Pike (past 90°)': 6, 'Consecutive Hurdlers': 6,
    // L7 — Advanced athletic jumps
    'Toe Touch (hyperextended)': 7, 'Switch Leap': 7, 'All 3 Splits (flat)': 7,
    'Five-count Jump Series': 7,
    // L8 — Combinations and elite flexibility
    'Toe Touch to Toe Touch': 8, 'Pike to Toe Touch Series': 8, 'Switch to Toe Touch': 8,
    'Needle Kick': 8, 'Standing Needle': 8,
    // L9 — Triple combos and crossovers
    'Triple Jump Combo': 9, 'All Splits Past Flat': 9,
    'Jump to Tumbling Pass': 9, 'Standing Back Tuck from Toe Touch': 9,
    // L6 — Performance (not a jump difficulty)
    'Jump Section Choreography': 6,
  },
};

// ---------------------------------------------------------------------------
// DANCE & CHOREOGRAPHY
// Progression: Motions → Turns → Leaps → Routines → Choreography
// ---------------------------------------------------------------------------

const DANCE_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Basic Motions (High V, T, Touchdown)', '8-Count Combination', 'Counting to Music', 'Basic Turns (half turn)'],
      mastery_suggestions: ['Full Turn (single)', 'Cheer Motion Technique', 'Short Dance Routine (16 counts)', 'Basic Leap'],
      optional: ['Creative Movement', 'Pom Technique', 'Hip Hop Basics'],
    },
    Intermediate: {
      required_suggestions: ['Full Turn (single)', 'Clean Motion Technique', 'Chassé & Leap', 'Calypso'],
      mastery_suggestions: ['Double Turn', '32-Count Dance Routine', 'Performance Presence', 'Synchronized Group Work'],
      optional: ['Jazz Technique', 'Contemporary Movement', 'Kick Line Technique'],
    },
    Advanced: {
      required_suggestions: ['Double Turn', 'Calypso', 'Extended Dance Routine (64+ counts)', 'Switch Leap'],
      mastery_suggestions: ['Triple Turn', 'Illusion', 'Full Routine Choreography', 'Style Versatility'],
      optional: ['Fouetté Turns', 'Improvisation'],
    },
    Elite: {
      required_suggestions: ['Triple Turn', 'Full Routine Choreography', 'Multi-Style Performance', 'Competition-Level Technique'],
      mastery_suggestions: ['Routine Design and Teaching', 'Advanced Floor Work', 'Emotional Performance Quality'],
      optional: ['Fouetté Series', 'Dance-Tumbling Combos', 'Aerial (tumbling crossover)'],
    },
  },
  categories: {
    'Cheer Motions': ['Basic Motions (High V, T, Touchdown)', 'Cheer Motion Technique', 'Clean Motion Technique', 'Sharp Motion Execution'],
    'Turns': ['Basic Turns (half turn)', 'Full Turn (single)', 'Double Turn', 'Triple Turn', 'Fouetté Turns', 'Fouetté Series', 'Illusion'],
    'Leaps & Jumps': ['Basic Leap', 'Chassé & Leap', 'Calypso', 'Switch Leap'],
    'Routines': ['8-Count Combination', 'Short Dance Routine (16 counts)', '32-Count Dance Routine', 'Extended Dance Routine (64+ counts)', 'Full Routine Choreography'],
    'Performance': ['Counting to Music', 'Performance Presence', 'Synchronized Group Work', 'Emotional Performance Quality', 'Style Versatility'],
  },
  difficulty: {
    // L1 — Basic motions
    'Basic Motions (High V, T, Touchdown)': 1, 'Basic Turns (half turn)': 1,
    // L2 — Entry-level
    '8-Count Combination': 2, 'Counting to Music': 2, 'Cheer Motion Technique': 2,
    'Pom Technique': 2, 'Hip Hop Basics': 2, 'Creative Movement': 2,
    // L3 — Beginner mastery
    'Full Turn (single)': 3, 'Short Dance Routine (16 counts)': 3, 'Basic Leap': 3,
    'Clean Motion Technique': 3, 'Kick Line Technique': 3,
    // L4 — Intermediate
    'Chassé & Leap': 4, '32-Count Dance Routine': 4,
    'Performance Presence': 4, 'Synchronized Group Work': 4,
    'Jazz Technique': 4, 'Contemporary Movement': 4, 'Sharp Motion Execution': 4,
    // L5 — Extended dance
    'Extended Dance Routine (64+ counts)': 5, 'Improvisation': 5,
    // L6 — Advanced
    'Double Turn': 6, 'Calypso': 6, 'Switch Leap': 6, 'Style Versatility': 6,
    // L7 — Advanced mastery
    'Triple Turn': 7, 'Multi-Style Performance': 7, 'Competition-Level Technique': 7,
    'Emotional Performance Quality': 7,
    // L8 — Elite
    'Illusion': 8, 'Full Routine Choreography': 8, 'Advanced Floor Work': 8,
    'Dance-Tumbling Combos': 8,
    // L9 — Championship
    'Fouetté Turns': 9, 'Routine Design and Teaching': 9, 'Fouetté Series': 9,
    'Aerial (tumbling crossover)': 9,
  },
};

// ---------------------------------------------------------------------------
// CONDITIONING & STRENGTH
// Progression: Basic holds → Bodyweight → Advanced strength → Power training
// ---------------------------------------------------------------------------

const CONDITIONING_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Plank Hold (30 sec)', 'V-Up (5 reps)', 'Push-Ups (10)', 'Hollow Body Hold (15 sec)'],
      mastery_suggestions: ['Plank Hold (60 sec)', 'V-Up (15 reps)', 'Push-Ups (20)', 'Hollow Body Hold (30 sec)'],
      optional: ['Bear Crawl', 'Crab Walk', 'Squat Jumps (10)'],
    },
    Intermediate: {
      required_suggestions: ['Plank Hold (90 sec)', 'V-Up (25 reps)', 'Push-Ups (30)', 'Rope Climb (1 length)'],
      mastery_suggestions: ['Pull-Up (3 reps)', 'Handstand Hold (15 sec)', 'Lunge Series (each leg)', 'Box Jumps (24 in)'],
      optional: ['Burpees (20)', 'Mountain Climbers (60 sec)', 'Resistance Band Work'],
    },
    Advanced: {
      required_suggestions: ['Pull-Up (8 reps)', 'Handstand Hold (30 sec)', 'Press Handstand', 'Power Squat Series'],
      mastery_suggestions: ['Pull-Up (15 reps)', 'Handstand Push-Up', 'Plyometric Box Series', 'Sprint Intervals'],
      optional: ['Weighted Exercises', 'Agility Ladder Drills', 'Battle Ropes'],
    },
    Elite: {
      required_suggestions: ['Handstand Push-Up (5 reps)', 'Muscle-Up (3 reps)', 'Advanced Plyometric Combos', 'Full Body Circuit (timed)'],
      mastery_suggestions: ['Full Practice Without Performance Decline', 'Plyometric Push-Up (10 reps)', 'Proper Warm-Up and Cool-Down Protocol'],
      optional: ['Olympic Lift Basics', 'Advanced Agility Circuits'],
    },
  },
  categories: {
    'Core Strength': ['Plank Hold (30 sec)', 'Plank Hold (60 sec)', 'Plank Hold (90 sec)', 'V-Up', 'Hollow Body Hold', 'Pike-Up'],
    'Upper Body': ['Push-Ups', 'Pull-Up', 'Handstand Hold', 'Handstand Push-Up', 'Press Handstand', 'Muscle-Up', 'Rope Climb'],
    'Lower Body': ['Squat Jumps', 'Lunge Series', 'Box Jumps', 'Power Squat Series', 'Plyometric Box Series'],
    'Cardio & Endurance': ['Burpees', 'Mountain Climbers', 'Sprint Intervals', 'Full Body Circuit', 'Battle Ropes'],
    'Flexibility & Recovery': ['Dynamic Stretching', 'Static Stretching', 'Foam Rolling', 'Proper Warm-Up and Cool-Down Protocol'],
  },
  difficulty: {
    // L1 — Entry conditioning
    'Plank Hold (30 sec)': 1, 'Crab Walk': 1,
    // L2 — Basic strength
    'V-Up (5 reps)': 2, 'Push-Ups (10)': 2, 'Hollow Body Hold (15 sec)': 2,
    'Bear Crawl': 2, 'Squat Jumps (10)': 2,
    // L3 — Beginner mastery
    'Plank Hold (60 sec)': 3, 'V-Up (15 reps)': 3, 'Push-Ups (20)': 3, 'Hollow Body Hold (30 sec)': 3,
    'Mountain Climbers (60 sec)': 3, 'Proper Warm-Up and Cool-Down Protocol': 3,
    // L4 — Intermediate
    'Plank Hold (90 sec)': 4, 'Push-Ups (30)': 4, 'Box Jumps (24 in)': 4,
    'Agility Ladder Drills': 4, 'Lunge Series (each leg)': 4, 'Resistance Band Work': 4,
    // L5 — Intermediate mastery
    'V-Up (25 reps)': 5, 'Pull-Up (3 reps)': 5, 'Handstand Hold (15 sec)': 5,
    'Rope Climb (1 length)': 5, 'Burpees (20)': 5, 'Sprint Intervals': 5,
    // L6 — Advanced basics
    'Power Squat Series': 6, 'Weighted Exercises': 6, 'Battle Ropes': 6, 'Press Handstand': 6,
    // L7 — Advanced
    'Pull-Up (8 reps)': 7, 'Handstand Push-Up': 7, 'Plyometric Box Series': 7,
    'Full Body Circuit (timed)': 7, 'Advanced Agility Circuits': 7,
    // L8 — Advanced mastery
    'Pull-Up (15 reps)': 8, 'Handstand Push-Up (5 reps)': 8, 'Muscle-Up (3 reps)': 8,
    'Advanced Plyometric Combos': 8, 'Plyometric Push-Up (10 reps)': 8,
    // L9 — Elite
    'Full Practice Without Performance Decline': 9, 'Olympic Lift Basics': 9,
  },
};

// ---------------------------------------------------------------------------
// GENERAL CHEER
// Progression: Motions → Jumps + Stunts → Tumbling + Routines → Competition
// ---------------------------------------------------------------------------

const GENERAL_CHEER_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Basic Motions (High V, T, Low V)', 'Simple Chants', 'Basic Jumps (Tuck, Spread Eagle)', 'Basic Stunt (Thigh Stand)'],
      mastery_suggestions: ['Toe Touch (T-level)', 'Short Cheer Routine', 'Prep-Level Stunt', 'Basic Cartwheel'],
      optional: ['Pom Routine', 'Spirit Chant', 'Group Cheer Sequence'],
    },
    Intermediate: {
      required_suggestions: ['Clean Motion Technique', 'Toe Touch (T-level)', 'Prep-Level Stunt', 'Short Routine (music)'],
      mastery_suggestions: ['Pike Jump', 'Extension Stunt', 'Round-off', 'Full Routine Performance'],
      optional: ['Cheer Dance Routine', 'Back Walkover', 'Crowd Leading'],
    },
    Advanced: {
      required_suggestions: ['Extension Stunt', 'Toe Touch (hyperextended)', 'Back Handspring', 'Full Routine with Transitions'],
      mastery_suggestions: ['Advanced Stunts (liberties)', 'Tumbling Pass', 'Competition-Ready Performance', 'Group Choreography'],
      optional: ['Basket Toss', 'Back Tuck', 'Advanced Dance Section'],
    },
    Elite: {
      required_suggestions: ['Competition-Level Routine', 'Advanced Stunts + Pyramids', 'Running Tumbling Pass (BHS to Tuck minimum)', 'Jump Section Combo'],
      mastery_suggestions: ['Full-Ups', 'Standing Tumbling Series', 'Elite Performance Quality', 'Choreography Leadership'],
      optional: ['Rewind or Full-Up Stunt', 'Layout or Full Twist Pass', 'Creative Choreography'],
    },
  },
  categories: {
    'Motions & Chants': ['Basic Motions (High V, T, Low V)', 'Simple Chants', 'Clean Motion Technique', 'Spirit Chant', 'Crowd Leading'],
    'Jumps': ['Basic Jumps (Tuck, Spread Eagle)', 'Toe Touch (T-level)', 'Pike Jump', 'Toe Touch (hyperextended)', 'Jump Section Combo'],
    'Stunts': ['Basic Stunt (Thigh Stand)', 'Prep-Level Stunt', 'Extension Stunt', 'Advanced Stunts (liberties)', 'Full-Ups', 'Basket Toss'],
    'Tumbling': ['Basic Cartwheel', 'Round-off', 'Back Walkover', 'Back Handspring', 'Back Tuck', 'Tumbling Pass'],
    'Routines': ['Short Cheer Routine', 'Short Routine (music)', 'Full Routine Performance', 'Competition-Level Routine', 'Full Routine with Transitions'],
  },
  difficulty: {
    // L1 — Entry
    'Basic Motions (High V, T, Low V)': 1, 'Simple Chants': 1, 'Spirit Chant': 1,
    // L2 — Basics
    'Basic Jumps (Tuck, Spread Eagle)': 2, 'Basic Stunt (Thigh Stand)': 2, 'Basic Cartwheel': 2, 'Pom Routine': 2,
    // L3 — Beginner mastery
    'Toe Touch (T-level)': 3, 'Round-off': 3, 'Clean Motion Technique': 3,
    'Short Cheer Routine': 3, 'Crowd Leading': 3, 'Group Cheer Sequence': 3, 'Pike Jump': 3,
    // L4 — Intermediate
    'Prep-Level Stunt': 4, 'Short Routine (music)': 4,
    'Cheer Dance Routine': 4, 'Back Walkover': 4,
    // L5 — Intermediate mastery
    'Full Routine Performance': 5,
    // L6 — Advanced
    'Extension Stunt': 6, 'Basket Toss': 6, 'Full Routine with Transitions': 6,
    'Group Choreography': 6, 'Advanced Dance Section': 6,
    // L7 — Advanced mastery
    'Back Handspring': 7, 'Toe Touch (hyperextended)': 7, 'Advanced Stunts (liberties)': 7,
    'Tumbling Pass': 7, 'Competition-Ready Performance': 7, 'Jump Section Combo': 7,
    'Choreography Leadership': 7, 'Creative Choreography': 7,
    // L8 — Elite
    'Back Tuck': 8, 'Full-Ups': 8, 'Advanced Stunts + Pyramids': 8,
    'Running Tumbling Pass (BHS to Tuck minimum)': 8, 'Standing Tumbling Series': 8,
    'Elite Performance Quality': 8,
    // L9 — Championship
    'Competition-Level Routine': 9, 'Rewind or Full-Up Stunt': 9, 'Layout or Full Twist Pass': 9,
  },
};

// ---------------------------------------------------------------------------
// BACKSPOT & BASES
// Progression: Grips → Base Technique → Catching → Toss Technique → Transitions
// ---------------------------------------------------------------------------

const BACKSPOT_BASES_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Proper Grip Technique', 'Thigh Stand Base/Back', 'Shoulder Sit Base/Back', 'Loading Position'],
      mastery_suggestions: ['Prep-Level Base/Back', 'Cradle Catch Technique', 'Toss Hands Grip', 'Dip and Drive Timing'],
      optional: ['Spotting Basics', 'Safety Positioning', 'Verbal Cues'],
    },
    Intermediate: {
      required_suggestions: ['Extension Base/Back', 'Cradle from Extension', 'Reload Extension', 'Liberty Base/Back Spot'],
      mastery_suggestions: ['Twist Cradle Catch', 'Walk-In Stunt', 'Group Timing', 'Sponge Technique'],
      optional: ['Two-Base Stunts', 'Transition Footwork', 'Pop-Down Technique'],
    },
    Advanced: {
      required_suggestions: ['Full-Up Toss Base/Back', 'Twist Cradle Catch', 'Tick-Tock Base/Back', 'Full-Around Base/Back'],
      mastery_suggestions: ['Full-Down Catch', 'Basket Toss Technique', 'Advanced Load Positions', 'Show-and-Go Technique'],
      optional: ['One-Base Stunts', 'Pyramid Connections'],
    },
    Elite: {
      required_suggestions: ['Rewind Base/Back', 'Double-Down Catch', 'Kick Full Basket Catch', '2-2-1 Pyramid Base/Back'],
      mastery_suggestions: ['Double-Up Toss', 'Elite Basket Tosses', 'Multi-Stunt Combos', 'Competition-Level Execution'],
      optional: ['Creative Entries', 'Elite Pyramid Skills', 'Specialty Transitions'],
    },
  },
  categories: {
    'Grips & Fundamentals': ['Proper Grip Technique', 'Loading Position', 'Dip and Drive Timing', 'Safety Positioning', 'Verbal Cues', 'Spotting Basics'],
    'Base Technique': ['Thigh Stand Base/Back', 'Shoulder Sit Base/Back', 'Prep-Level Base/Back', 'Extension Base/Back', 'Liberty Base/Back Spot'],
    'Catching': ['Cradle Catch Technique', 'Twist Cradle Catch', 'Full-Down Catch', 'Double-Down Catch', 'Kick Full Basket Catch'],
    'Toss Technique': ['Toss Hands Grip', 'Full-Up Toss Base/Back', 'Basket Toss Technique', 'Double-Up Toss'],
    'Transitions': ['Reload Extension', 'Walk-In Stunt', 'Tick-Tock Base/Back', 'Show-and-Go Technique', 'Multi-Stunt Combos',
      'Full-Around Base/Back', 'Pop-Down Technique', 'Sponge Technique', 'Rewind Base/Back'],
  },
  difficulty: {
    // L1 — Fundamentals
    'Proper Grip Technique': 1, 'Thigh Stand Base/Back': 1, 'Shoulder Sit Base/Back': 1,
    'Spotting Basics': 1, 'Safety Positioning': 1,
    // L2 — Basic technique
    'Loading Position': 2, 'Dip and Drive Timing': 2, 'Toss Hands Grip': 2, 'Verbal Cues': 2,
    // L3 — Prep-level basing
    'Prep-Level Base/Back': 3, 'Cradle Catch Technique': 3, 'Sponge Technique': 3, 'Pop-Down Technique': 3,
    // L4 — Intermediate
    'Cradle from Extension': 4, 'Walk-In Stunt': 4, 'Group Timing': 4, 'Transition Footwork': 4,
    // L5 — Extension basing
    'Extension Base/Back': 5, 'Reload Extension': 5, 'Two-Base Stunts': 5,
    // L6 — Advanced positions
    'Liberty Base/Back Spot': 6, 'Full-Around Base/Back': 6, 'Advanced Load Positions': 6,
    // L7 — Advanced entries and catches
    'Full-Up Toss Base/Back': 7, 'Twist Cradle Catch': 7, 'Tick-Tock Base/Back': 7,
    'Full-Down Catch': 7, 'Basket Toss Technique': 7, 'Show-and-Go Technique': 7, 'Pyramid Connections': 7,
    // L8 — Elite catches
    'Double-Down Catch': 8, 'One-Base Stunts': 8, 'Multi-Stunt Combos': 8,
    'Competition-Level Execution': 8, 'Creative Entries': 8, 'Specialty Transitions': 8,
    // L9 — Elite entries
    'Rewind Base/Back': 9, 'Double-Up Toss': 9, 'Kick Full Basket Catch': 9,
    'Elite Basket Tosses': 9, '2-2-1 Pyramid Base/Back': 9, 'Elite Pyramid Skills': 9,
  },
};

// ---------------------------------------------------------------------------
// PERFORMANCE SKILLS
// Progression: Stage Presence → Facials → Crowd Engagement → Competition Readiness
// ---------------------------------------------------------------------------

const PERFORMANCE_SKILLS: CategorySkillData = {
  levels: {
    Beginner: {
      required_suggestions: ['Confidence on Stage', 'Eye Contact with Audience', 'Smile & Energy', 'Basic Formation Changes'],
      mastery_suggestions: ['Voice Projection', 'Synchronized Movement', 'Basic Facials & Expressions', 'Count Awareness'],
      optional: ['Performance in Front of Mirror', 'Video Self-Review', 'Mirror Work'],
    },
    Intermediate: {
      required_suggestions: ['Facials & Crowd Engagement', 'Formation Transitions', 'Sustain Energy Through Full Routine', 'Team Synchronization'],
      mastery_suggestions: ['Advanced Facials', 'Crowd Leading Technique', 'Clean Routine Transitions', 'Performance Under Pressure'],
      optional: ['Performing with a Theme', 'Theme Interpretation', 'Solo Spotlight'],
    },
    Advanced: {
      required_suggestions: ['Competition-Level Facials', 'Seamless Transitions', 'Full Routine Stamina', 'Showmanship'],
      mastery_suggestions: ['Judge Engagement', 'Error Recovery', 'Full-Routine Energy Control', 'Performance Leadership'],
      optional: ['Choreography Interpretation', 'Developing Signature Style'],
    },
    Elite: {
      required_suggestions: ['Elite Showmanship', 'Competition Mentality', 'Routine Polish', 'Elite Judge Engagement'],
      mastery_suggestions: ['Team Energy Leadership', 'Pre-Competition Visualization', 'Leads Warm-Up or Runthrough'],
      optional: ['Performance Analysis', 'Creative Expression'],
    },
  },
  categories: {
    'Stage Presence': ['Confidence on Stage', 'Eye Contact with Audience', 'Smile & Energy', 'Showmanship', 'Elite Showmanship'],
    'Facials & Expression': ['Basic Facials & Expressions', 'Facials & Crowd Engagement', 'Advanced Facials', 'Competition-Level Facials'],
    'Crowd Engagement': ['Voice Projection', 'Crowd Leading Technique', 'Judge Engagement', 'Elite Judge Engagement'],
    'Teamwork': ['Synchronized Movement', 'Team Synchronization', 'Performance Leadership', 'Team Energy Leadership'],
    'Competition Readiness': ['Performance Under Pressure', 'Error Recovery', 'Competition Mentality', 'Pre-Competition Visualization',
      'Choreography Interpretation', 'Performance Analysis', 'Creative Expression'],
  },
  difficulty: {
    // L1 — Foundation
    'Confidence on Stage': 1, 'Eye Contact with Audience': 1, 'Smile & Energy': 1, 'Video Self-Review': 1,
    // L2 — Beginner skills
    'Voice Projection': 2, 'Basic Facials & Expressions': 2, 'Basic Formation Changes': 2,
    'Count Awareness': 2, 'Performance in Front of Mirror': 2, 'Mirror Work': 2,
    // L3 — Group awareness
    'Performing with a Theme': 3,
    // L4 — Intermediate
    'Facials & Crowd Engagement': 4, 'Synchronized Movement': 4, 'Formation Transitions': 4,
    'Sustain Energy Through Full Routine': 4, 'Crowd Leading Technique': 4, 'Theme Interpretation': 4,
    // L5 — Intermediate mastery
    'Team Synchronization': 5, 'Advanced Facials': 5, 'Clean Routine Transitions': 5,
    'Showmanship': 5, 'Solo Spotlight': 5, 'Developing Signature Style': 5,
    // L6 — Advanced
    'Competition-Level Facials': 6, 'Seamless Transitions': 6, 'Full Routine Stamina': 6,
    'Judge Engagement': 6, 'Performance Under Pressure': 6, 'Full-Routine Energy Control': 6,
    'Choreography Interpretation': 6, 'Performance Analysis': 6, 'Creative Expression': 6,
    // L7 — Advanced mastery
    'Performance Leadership': 7, 'Routine Polish': 7, 'Pre-Competition Visualization': 7,
    // L8 — Elite
    'Elite Showmanship': 8, 'Error Recovery': 8, 'Competition Mentality': 8,
    'Elite Judge Engagement': 8, 'Team Energy Leadership': 8, 'Leads Warm-Up or Runthrough': 8,
  },
};


// ---------------------------------------------------------------------------
// MASTER MAP
// ---------------------------------------------------------------------------

export const SKILL_DATABASE: Record<ClassCategory, CategorySkillData> = {
  'Tumbling': TUMBLING_SKILLS,
  'Stunting': STUNTING_SKILLS,
  'Flyer': FLYER_SKILLS,
  'Jumps & Flexibility': JUMPS_SKILLS,
  'Dance & Choreography': DANCE_SKILLS,
  'Conditioning & Strength': CONDITIONING_SKILLS,
  'General Cheer': GENERAL_CHEER_SKILLS,
  'Backspot & Bases': BACKSPOT_BASES_SKILLS,
  'Performance Skills': PERFORMANCE_SKILLS,
};

/**
 * Get skill options for a given category, flattened into a searchable list.
 */
export function getSkillOptionsForCategory(category: ClassCategory): SkillOption[] {
  const data = SKILL_DATABASE[category];
  if (!data) return [];

  const options: SkillOption[] = [];
  const seen = new Set<string>();

  Object.entries(data.categories).forEach(([groupName, skills]) => {
    skills.forEach(skill => {
      if (seen.has(skill)) return;
      seen.add(skill);
      options.push({
        name: skill,
        category: groupName,
        difficulty: data.difficulty[skill] ?? 5,
      });
    });
  });

  return options;
}

/**
 * Get level-specific suggestions for a given category + level.
 */
export function getLevelSuggestions(
  category: ClassCategory,
  level: string | undefined | null,
  type: 'required' | 'mastery'
): readonly string[] {
  const data = SKILL_DATABASE[category];
  if (!data || !level) return [];

  const levelData = data.levels[level];
  if (!levelData) return [];

  return type === 'required' ? levelData.required_suggestions : levelData.mastery_suggestions;
}
